// Aircraft create / edit form: loads the aircraft, holds the form state and saves it.
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Form, Input, Space, Spin, Typography, App as AntdApp } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { createAircraft, getAircraft, listOperators, updateAircraft } from '../../api/endpoints';
import { useSingleSubmit } from '../../hooks/useSingleSubmit';
import { LIMITS } from '../../data/limits';
import type { Photo, Range, Uplift } from './aircraftFormRows';
import { GeneralDetailsCard } from './GeneralDetailsCard';
import { EligibilityCard } from './EligibilityCard';
import { PricesCard } from './PricesCard';

const { Title } = Typography;

const DEFAULT_POLICY =
  'Cancellations more than 14 days before departure are refunded in full. ' +
  'Within 14 days, a portion of the trip cost may be retained per the cancellation table in the booking confirmation.';

export function AircraftFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const nav = useNavigate();
  const qc = useQueryClient();
  const { message } = AntdApp.useApp();

  const [form] = Form.useForm();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [mainPhotoUrl, setMainPhotoUrl] = useState<string | null>(null);
  const [ranges, setRanges] = useState<Range[]>([]);
  const [uplifts, setUplifts] = useState<Uplift[]>([]);

  const operators = useQuery({
    queryKey: ['operators', 'all-for-aircraft'],
    queryFn: () => listOperators({ page: 1, pageSize: 100 }),
  });

  const existing = useQuery({
    queryKey: ['aircraft', id],
    queryFn: () => getAircraft(id!),
    enabled: editing,
  });

  useEffect(() => {
    if (!editing) {
      form.setFieldsValue({
        // SHAR-442 — Inactive is the default; only a complete aircraft can be
        // saved as Active (the backend rejects an incomplete Active aircraft).
        status: 'inactive',
        confirmationDeadlineDays: 7,
        cancellationPolicy: DEFAULT_POLICY,
      });
      return;
    }
    const a = existing.data;
    if (!a) return;
    form.setFieldsValue({
      operatorId: a.operatorId,
      tailNumber: a.tailNumber,
      modelName: a.modelName,
      category: a.category,
      seatingCapacity: a.seatingCapacity,
      yearOfManufacture: a.yearOfManufacture ?? undefined,
      yearOfRefurbishment: a.yearOfRefurbishment ?? undefined,
      ownersApproval: a.ownersApproval ?? false,
      maxRangeNm: a.maxRangeNm,
      cruiseSpeedKnots: a.cruiseSpeedKnots,
      homebaseAirportCode: a.homebaseAirportCode,
      homebaseAirportName: a.homebaseAirportName ?? undefined,
      maxFerryDistanceNm: a.maxFerryDistanceNm,
      confirmationDeadlineDays: a.confirmationDeadlineDays,
      cancellationPolicy: a.cancellationPolicy ?? DEFAULT_POLICY,
      status: a.status,
      currencyCode: a.currencyCode ?? undefined,
      occupiedHourlyRate: a.occupiedHourlyRate != null ? Number(a.occupiedHourlyRate) : undefined,
      ferryHourlyRate: a.ferryHourlyRate != null ? Number(a.ferryHourlyRate) : undefined,
      contingencyTimeMin: a.contingencyTimeMin ?? undefined,
      minSectorTimeHours: a.minSectorTimeHours != null ? Number(a.minSectorTimeHours) : undefined,
      minTripRevenue: a.minTripRevenue != null ? Number(a.minTripRevenue) : undefined,
    });
    setPhotos((a.photos || []).map((p) => ({ url: p.url })));
    setMainPhotoUrl(a.mainPhotoUrl);
    setRanges(
      (a.blackoutRanges || []).map((r) => ({
        fromDate: dayjs(r.fromDate),
        toDate: dayjs(r.toDate),
        reason: r.reason ?? '',
      })),
    );
    setUplifts(
      (a.peakUpliftMultipliers || []).map((m) => ({
        fromDate: dayjs(m.fromDate),
        toDate: dayjs(m.toDate),
        multiplier: Number(m.multiplier),
      })),
    );
  }, [editing, existing.data, form]);

  const createMut = useMutation({
    mutationFn: createAircraft,
    onSuccess: () => {
      message.success('Aircraft created');
      qc.invalidateQueries({ queryKey: ['aircraft'] });
      nav('/aircraft');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Create failed'),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => updateAircraft(id, body),
    onSuccess: () => {
      message.success('Aircraft updated');
      qc.invalidateQueries({ queryKey: ['aircraft'] });
      nav('/aircraft');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Update failed'),
  });

  // Regression 28 Sep — one submit at a time (a double click sent 2 PATCHes).
  const submitGuard = useSingleSubmit();
  const onSubmit = submitGuard.wrap(async () => {
    const v = await form.validateFields();
    const body: any = {
      ...v,
      mainPhotoUrl: mainPhotoUrl ?? null,
      photos: photos.map((p) => ({ url: p.url })),
      blackoutRanges: ranges
        .filter((r) => r.fromDate && r.toDate)
        .map((r) => ({
          fromDate: r.fromDate!.format('YYYY-MM-DD'),
          toDate: r.toDate!.format('YYYY-MM-DD'),
          reason: r.reason || null,
        })),
      peakUpliftMultipliers: uplifts
        .filter((u) => u.fromDate && u.toDate && u.multiplier != null)
        .map((u) => ({
          fromDate: u.fromDate!.format('YYYY-MM-DD'),
          toDate: u.toDate!.format('YYYY-MM-DD'),
          multiplier: u.multiplier,
        })),
    };
    if (editing && id) await updateMut.mutateAsync({ id, body });
    else await createMut.mutateAsync(body);
  });

  const goBackToList = () => {
    if (window.history.length > 1) nav(-1);
    else nav('/aircraft');
  };

  // Regression 28 Sep — an unknown id showed an empty edit form (and a Save
  // that could only fail). Say so instead, as the booking page does.
  if (editing && !existing.data) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={goBackToList}>
          Back
        </Button>
        <div style={{ textAlign: 'center', padding: 48 }}>
          {existing.isLoading ? <Spin /> : <Typography.Text type="secondary">Aircraft not found.</Typography.Text>}
        </div>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space>
        {/* SHAR-1010 — Back returns to the list AS IT WAS, page included. It
            used to navigate to /aircraft, which is page 1 whatever page the
            aircraft was actually on. Going back through history restores the
            list's own address (the page is in the URL now); a form opened
            directly by link has nothing to go back to, so it falls back to the
            list. */}
        <Button icon={<ArrowLeftOutlined />} onClick={goBackToList}>
          Back
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {editing ? `Edit aircraft${existing.data ? ` — ${existing.data.tailNumber}` : ''}` : 'New aircraft'}
        </Title>
      </Space>

      <Form form={form} layout="vertical">
        <GeneralDetailsCard
          form={form}
          operators={operators}
          mainPhotoUrl={mainPhotoUrl}
          setMainPhotoUrl={setMainPhotoUrl}
          photos={photos}
          setPhotos={setPhotos}
        />

        <EligibilityCard form={form} ranges={ranges} setRanges={setRanges} />

        <PricesCard uplifts={uplifts} setUplifts={setUplifts} />

        <Card title="Cancellation policy" style={{ marginBottom: 16 }}>
          <Form.Item name="cancellationPolicy">
            <Input.TextArea maxLength={LIMITS.text} showCount rows={4} />
          </Form.Item>
        </Card>

        <Space>
          <Button onClick={goBackToList}>Cancel</Button>
          <Button
            type="primary"
            loading={submitGuard.busy || createMut.isPending || updateMut.isPending}
            onClick={onSubmit}
          >
            {editing ? 'Save changes' : 'Create aircraft'}
          </Button>
        </Space>
      </Form>
    </Space>
  );
}
